// Affidavit picker: categories in the same order as www.beeregistry.co.za/affidavit.asp.
// Turnover limits are copied from the affidavits themselves (backend/affidavits/<slug>.pdf).
// Page images and the signature / date positions live in layout.json (scripts/prepare_affidavits.py).
import LAYOUT from "@/data/layout.json";

export { LAYOUT };

export const CATEGORIES = [
  {
    key: "general",
    title: "Generic Codes",
    intro: [
      "The Generic Codes of Good Practice apply to every business that is not covered by one of the sector codes below - this is most businesses. For example: retailers and wholesalers, manufacturers, professional and consulting services, medical and other healthcare practices, schools and training providers, security and cleaning companies, and repair and maintenance businesses.",
      "If your business falls under a sector code that is not listed on this page (for example Transport, Forestry or Chartered Accountancy), please ask TC Auditors which affidavit to use.",
    ],
    limits: [
      { kind: "EME", text: "annual total revenue of R10 million or less." },
      { kind: "QSE", text: "annual total revenue between R10 million and R50 million." },
    ],
    items: [
      { slug: "general-eme", label: "Generic EME" },
      { slug: "general-qse", label: "Generic QSE" },
    ],
  },
  {
    key: "agri",
    title: "Agri-BEE",
    intro: [
      "The AgriBEE Sector Code applies to businesses in the agricultural value chain: farms of every kind (crops, fruit, vegetables, grain, sugar cane, livestock, dairy, poultry, game and fish farming), businesses that mainly process farm products (for example abattoirs, mills, packhouses and other agro-processors), and businesses that mainly supply farming inputs or trade in farm produce (for example seed, fertiliser and animal feed suppliers, and produce traders).",
    ],
    limits: [
      { kind: "EME", text: "annual total revenue of R10 million or less." },
      { kind: "QSE", text: "annual total revenue between R10 million and R50 million." },
    ],
    items: [
      { slug: "agri-eme", label: "Agri-BEE EME" },
      { slug: "agri-qse", label: "Agri-BEE QSE" },
    ],
  },
  {
    key: "construction",
    title: "Construction",
    intro: [
      "The Construction Sector Code applies to two kinds of businesses. Contractors do the building work itself: general building contractors, civil works and road builders, and specialist trades on construction projects such as electrical, plumbing, roofing and painting contractors. BEPs (Built Environment Professionals) provide professional services for construction projects: architects, engineers, quantity surveyors, project and construction managers, town planners and landscape architects.",
      "Only EME affidavits are available for this sector.",
    ],
    limits: [
      { kind: "EME", who: "Contractors", text: "annual total revenue of R3 million or less." },
      { kind: "EME", who: "BEP (Built Environment Professionals, e.g. architects, engineers, quantity surveyors)", text: "annual total revenue of R1.8 million or less." },
    ],
    items: [
      { slug: "construction-contractor-eme", label: "Contractor EME" },
      { slug: "construction-bep-eme", label: "BEP EME" },
    ],
  },
  {
    key: "finance",
    title: "Finance",
    intro: [
      "The Financial Sector Code applies to businesses that provide financial services. For example: banks, long-term and short-term insurers, insurance brokers and financial advisers (authorised financial services providers), asset and investment managers, stockbrokers, retirement fund administrators and other financial institutions.",
    ],
    limits: [
      { kind: "EME", text: "annual total revenue of R10 million or less." },
      { kind: "QSE", text: "annual total revenue between R10 million and R50 million." },
    ],
    items: [
      { slug: "finance-eme", label: "Finance EME" },
      { slug: "finance-qse", label: "Finance QSE" },
    ],
  },
  {
    key: "ict",
    title: "ICT",
    intro: [
      "The ICT (Information and Communication Technology) Sector Code applies to technology and communication businesses. For example: software developers, IT services, consulting and support companies, computer hardware and electronics businesses, telecommunications companies, internet service providers and broadcasters.",
    ],
    limits: [
      { kind: "EME", text: "annual total revenue of R10 million or less." },
      { kind: "QSE", text: "annual total revenue between R10 million and R50 million." },
    ],
    items: [
      { slug: "ict-eme", label: "ICT EME" },
      { slug: "ict-qse", label: "ICT QSE" },
    ],
  },
  {
    key: "mac",
    title: "MAC",
    intro: [
      "The MAC (Marketing, Advertising and Communication) Sector Code applies to businesses that create and place marketing for others. For example: advertising and creative agencies, marketing and communication agencies, digital agencies, and media planning and buying businesses.",
      "Public Relations (PR) businesses fall under the same code, but have their own, lower limits and their own affidavits.",
    ],
    limits: [
      { kind: "EME", who: "MAC", text: "annual total revenue of R10 million or less." },
      { kind: "QSE", who: "MAC", text: "annual total revenue between R10 million and R50 million." },
      { kind: "EME", who: "Public Relations", text: "annual total revenue of R5 million or less." },
      { kind: "QSE", who: "Public Relations", text: "annual total revenue between R5 million and R10 million." },
    ],
    items: [
      { slug: "mac-eme", label: "MAC EME" },
      { slug: "mac-qse", label: "MAC QSE" },
      { slug: "mac-pr-eme", label: "Public Relations EME" },
      { slug: "mac-pr-qse", label: "Public Relations QSE" },
    ],
  },
  {
    key: "property",
    title: "Property",
    intro: [
      "The Property Sector Code applies to businesses that own, develop, manage or sell property. There are three kinds, each with its own affidavits. Asset Based: businesses that own property and earn their income from it, such as property-owning companies, landlords, developers and REITs (measured on net asset value, not turnover). Estate Agents: residential and commercial estate agencies. Service Based: businesses that provide property services, such as property managers, managing agents, valuers and property brokers.",
    ],
    limits: [
      { kind: "EME", who: "Asset Based", text: "net asset value of R80 million or less." },
      { kind: "QSE", who: "Asset Based", text: "net asset value of less than R400 million." },
      { kind: "EME", who: "Estate Agents", text: "annual total revenue of R2.5 million or less." },
      { kind: "QSE", who: "Estate Agents", text: "annual total revenue between R2.5 million and R35 million." },
      { kind: "EME", who: "Service Based", text: "annual total revenue of R10 million or less." },
      { kind: "QSE", who: "Service Based", text: "annual total revenue between R10 million and R50 million." },
    ],
    items: [
      { slug: "property-asset-eme", label: "Asset Based EME" },
      { slug: "property-asset-qse", label: "Asset Based QSE" },
      { slug: "property-estate-eme", label: "Estate Agents EME" },
      { slug: "property-estate-qse", label: "Estate Agents QSE" },
      { slug: "property-service-eme", label: "Service Based EME" },
      { slug: "property-service-qse", label: "Service Based QSE" },
    ],
  },
  {
    key: "specialised",
    title: "Specialised Entities",
    intro: [
      "Specialised Entities are organisations that do not exist to make a profit for owners, so they are measured on their black beneficiaries instead of black ownership. For example: non-profit companies (NPCs), non-profit organisations (NPOs), public benefit organisations (PBOs), charitable trusts and similar entities such as public entities. Their size is measured on total revenue, allocated budget or gross receipts.",
    ],
    limits: [
      { kind: "EME", text: "R10 million or less." },
      { kind: "QSE", text: "between R10 million and R50 million." },
    ],
    items: [
      { slug: "specialised-eme", label: "Specialised EME" },
      { slug: "specialised-qse", label: "Specialised QSE" },
    ],
  },
  {
    key: "tourism",
    title: "Tourism",
    intro: [
      "The Tourism Sector Code applies to businesses in the tourism industry: accommodation (for example hotels, lodges, guest houses, B&Bs and self-catering establishments), hospitality and related services (for example conference venues and tourist attractions), and travel and related services (for example tour operators, travel agencies, tour guides and car rental companies).",
    ],
    limits: [
      { kind: "EME", text: "annual total revenue of R5 million or less." },
      { kind: "QSE", text: "annual total revenue between R5 million and R45 million." },
    ],
    items: [
      { slug: "tourism-eme", label: "Tourism EME" },
      { slug: "tourism-qse", label: "Tourism QSE" },
    ],
  },
];

export const KIND_NAMES = {
  EME: "Exempted Micro Enterprise",
  QSE: "Qualifying Small Enterprise",
};
